/**
 * Trigger node definition.
 * Device triggers are combined with condition nodes via combineTriggerCondition.
 * Standalone trigger with device builds device trigger JSON.
 */

import type { NodeDefinition, SceneJsonContext } from '../types';
import type { FlowNode } from '../../../types/flow-types';
import { createDeviceTriggerFromFlow } from '../../../converters/helpers/device-conditions';

export const triggerDefinition: NodeDefinition = {
	type: 'trigger',
	category: 'trigger',
	sourceHandles: ['output'],

	defaultData: () => ({
		label: 'Trigger',
		triggerType: 'device',
		executionMode: 'once',
	}),

	toSceneJson: (node: FlowNode) => {
		if (node.data.sceneData) {
			const sceneData = { ...(node.data.sceneData as any) };
			sceneData._fid = sceneData._fid || node.id;
			return sceneData;
		}
		if (node.data.originalCondition) {
			const original = { ...(node.data.originalCondition as any) };
			original._fid = original._fid || node.id;
			return original;
		}

		const device = node.data.device;
		if (!device?.id) return null;

		const rawMode = node.data.executionMode ?? node.data.conditionType?.id ?? 'once';
		const executionMode: 'once' | 'any' | 'repeat' =
			rawMode === 3 || rawMode === 'repeat'
				? 'repeat'
				: rawMode === 2 || rawMode === 'any'
					? 'any'
					: 'once';

		return createDeviceTriggerFromFlow(
			{
				deviceId: device.id,
				channel: device.channel ?? '0',
				property: node.data.property?.id || 'state',
				operator: node.data.operator || 'eq',
				value: node.data.value ?? 0,
			},
			executionMode,
			node.data.conditionType?.valid_for_min ?? 5,
		);
	},
};
